import { User } from "@firebase/auth-types"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { CollectionUtils } from "../../../core/collection"
import { Emotion, Title, Transfer } from "../../../types"
import { ConnectionStatus } from "../../../types/enum/ConnectionStatus"
import { Language } from "../../../types/enum/Language"
import { logger } from "../../../utils/logger"
import { getAvatarString } from "../pages/utils/avatar"
import { normalizeLanguage } from "../i18n"
import {
  IPokemonCollectionItemUnpacked,
  IUserMetadataJSON,
  IUserMetadataLean,
  IUserMetadataUnpacked
} from "../../../types/interfaces/UserMetadata"
import { INotification } from "../../../types/notifications"
import { webBasePath } from "../pages/utils/fetchConfigClient"
import { leaveAllRooms, rooms } from "../network.js"

export interface INetwork {
  uid: string
  displayName: string
  email: string
  profile: IUserMetadataUnpacked | undefined
  pendingGameId: string | null
  connectionStatus: ConnectionStatus
  error: string | null
  notifications: INotification[]
  pal: IUserMetadataLean | null
}


// let endpoint = `${window.location.protocol.replace("http", "ws")}//${window.location.host
//   }`
let endpoint = "";
if(webBasePath){
  endpoint = webBasePath.replace("http", "ws")
}
else{
  endpoint = `${window.location.protocol.replace("http", "ws")}//${
    window.location.host
  }`
}
logger.info(endpoint)

const initalState: INetwork = {
  uid: "",
  displayName: "",
  email: "",
  profile: undefined,
  pendingGameId: null,
  error: null,
  connectionStatus: ConnectionStatus.PENDING,
  notifications: [],
  pal: null
}

const networkSlice = createSlice({
  name: "network",
  initialState: initalState,
  reducers: {
    logIn: (state, action: PayloadAction<User>) => {
      if (action.payload) {
        state.uid = action.payload.uid
        state.displayName = action.payload.displayName ?? "Anonymous"
        state.email = action.payload.email ?? ""
      }
    },
    logOut: (state) => {
      state.uid = ""
      state.displayName = ""
      state.email = ""
      leaveAllRooms()
    },
    setProfile: (state, action: PayloadAction<IUserMetadataJSON>) => {
      const unpackedCollection: Map<string, IPokemonCollectionItemUnpacked> =
        new Map()
      for (const index in action.payload.pokemonCollection) {
        const item = action.payload.pokemonCollection[index]
        unpackedCollection.set(
          index,
          CollectionUtils.unpackCollectionItem(item)
        )
      }

      state.profile = {
        ...action.payload,
        language: normalizeLanguage(action.payload.language),
        pokemonCollection: unpackedCollection
      }
    },
    changeName: (state, action: PayloadAction<string>) => {
      if (state.profile) state.profile.displayName = action.payload
      rooms.lobby?.send(Transfer.CHANGE_NAME, { name: action.payload })
    },
    changeAvatar: (
      state,
      action: PayloadAction<{ index: string; emotion: Emotion; shiny: boolean }>
    ) => {
      if (state.profile)
        state.profile.avatar = getAvatarString(
          action.payload.index,
          action.payload.shiny,
          action.payload.emotion
        )
      rooms.lobby?.send(Transfer.CHANGE_AVATAR, action.payload)
    },
    changeSelectedEmotion: (
      state,
      action: PayloadAction<{
        index: string
        emotion: Emotion | null
        shiny: boolean
      }>
    ) => {
      if (state.profile) {
        const pokemonCollectionItem = state.profile.pokemonCollection.get(
          action.payload.index
        )
        if (pokemonCollectionItem) {
          pokemonCollectionItem.selectedEmotion = action.payload.emotion
          pokemonCollectionItem.selectedShiny = action.payload.shiny
        }
      }
      rooms.lobby?.send(Transfer.CHANGE_SELECTED_EMOTION, action.payload)
    },
    setTitle: (state, action: PayloadAction<Title | "">) => {
      if (state.profile) state.profile.title = action.payload
      rooms.lobby?.send(Transfer.SET_TITLE, action.payload)
    },
    useGiftCard: (
      state,
      action: PayloadAction<{ giftCardCode: string}>
    ) => {
      rooms.lobby?.send(Transfer.USE_GIFTCARD, action.payload)
    },
    createGiftCard: (
      state,
      action: PayloadAction<{ count: number}>
    ) => {
      rooms.lobby?.send(Transfer.CREATE_GIFTCARD, action.payload)
    },
    selectLanguage: (state, action: PayloadAction<Language>) => {
      const language = normalizeLanguage(action.payload)
      if (state.profile) state.profile.language = language
      rooms.lobby?.send(Transfer.SELECT_LANGUAGE, language)
    },
    setErrorAlertMessage: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    setConnectionStatus: (state, action: PayloadAction<ConnectionStatus>) => {
      state.connectionStatus = action.payload
    },
    setPendingGameId: (state, action: PayloadAction<string | null>) => {
      state.pendingGameId = action.payload
    },
    setNotifications: (state, action: PayloadAction<INotification[]>) => {
      state.notifications = action.payload
    },
    clearNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        (n) => n.id !== action.payload
      )
    },
    selectPal: (state, action: PayloadAction<string | null>) => {
      if (state.profile) {
        if(state.profile.uid === action.payload) return; // can't select yourself as pal
        state.profile.eventData = {
          ...(state.profile.eventData || {}),
          pal: action.payload || undefined
        }
      }
      rooms.lobby?.send(Transfer.SELECT_PAL, action.payload)
    },
    setPal: (state, action: PayloadAction<IUserMetadataLean | null>) => {
      state.pal = action.payload
    },
    confirmPal: (state, action: PayloadAction<string>) => {
      const playerIdThatChoseYou = action.payload
      if (
        state.pal &&
        state.pal?.uid === playerIdThatChoseYou &&
        state.profile
      ) {
        state.pal.eventData = {
          ...(state.pal.eventData || {}),
          pal: state.profile.uid
        }
      }
    }
  }
})

export const {
  useGiftCard,
  createGiftCard,
  logIn,
  logOut,
  setProfile,
  setNotifications,
  clearNotification,
  changeName,
  changeAvatar,
  changeSelectedEmotion,
  setTitle,
  selectLanguage,
  setConnectionStatus,
  setErrorAlertMessage,
  setPendingGameId,
  selectPal,
  setPal,
  confirmPal
} = networkSlice.actions

export default networkSlice.reducer
